import { Component, OnInit, OnDestroy, HostListener, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { SharedModule } from '../../../shared/shared.module';
import { BehaviorBipFunctionLinkModel, BehaviorBipInfoModel, BehaviorFunctionModel, PatientBehaviorModel, NameDescriptionModel } from '../../../core/api/models';
import {
    BipFunctionDisplayItem,
    compareBipFunctionRoleOrder,
    getBipFunctionCssClass,
    getBipFunctionRoleBadge,
    mapBehaviorBipFunctionsToDisplay
} from '../../bip/bip-function-display.util';
import { StaffApiService } from '../../../core/api/services';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';

interface BehaviorBipStrategyRow {
    clientKey: string;
    id?: number | null;
    name: string;
    description: string;
    customName?: string;
    patientBehaviorAntecedentID?: number | null;
    patientBehaviorConsequenceID?: number | null;
    patientBehaviorReplacementID?: number | null;
}

@Component({
    selector: 'app-behavior-bip-info',
    standalone: true,
    imports: [CommonModule, FormsModule, CKEditorModule, SelectButtonModule, MultiSelectModule, DropdownModule, SharedModule],
    templateUrl: './behavior-bip-info.component.html',
    styleUrl: './behavior-bip-info.component.scss'
})
export class BehaviorBipInfoComponent implements OnInit, OnDestroy {
    patientID = input.required<string>();
    selectedBehavior = input.required<PatientBehaviorModel>();
    behaviorID = input.required<number>();
    startCurrentView = input<boolean>(false);
    showDraftToggle = input<boolean>(true);
    showSaveButton = input<boolean>(true);
    canEditBip = input<boolean>(true);
    useOfflineData = input<boolean>(false);
    loading = signal<boolean>(false);
    isCurrentView: boolean = false;
    saving:boolean = false;
    // Draft/current single model for this behavior
    currentModel: BehaviorBipInfoModel | null = null;
    draftModel: BehaviorBipInfoModel | null = null;

    // Lookups (loaded via single bootstrap endpoint)
    functions = signal<BehaviorFunctionModel[]>([]);
    antecedentTemplates = signal<NameDescriptionModel[]>([]);
    consequenceTemplates = signal<NameDescriptionModel[]>([]);
    replacementTemplates = signal<NameDescriptionModel[]>([]);
    antecedentRows = signal<BehaviorBipStrategyRow[]>([]);
    consequenceRows = signal<BehaviorBipStrategyRow[]>([]);
    replacementRows = signal<BehaviorBipStrategyRow[]>([]);

    addingAntecedent: boolean = false;
    addingConsequence: boolean = false;
    addingReplacement: boolean = false;


    expandedFunctionId: number | null = null;

    // UI state
    showValidationMessage: boolean = false;
    Editor = Editor;
    editorConfig = editorConfig;
    private autoSaveHandle: any;
    private strategyRowKeySeed = 0;

    constructor(
        private staffApi: StaffApiService,
        private alerts: AlertService,
        private offlineDataService: OfflineDataService
    ) {}

    ngOnInit(): void {
        if (!this.canEditBip()) {
            this.isCurrentView = true;
        } else {
            this.isCurrentView = this.startCurrentView();
        }

        const useOffline = this.useOfflineData() || this.offlineDataService.isInOfflineMode();
        if (useOffline) {
            // Offline mode is read-only for this screen
            this.loadDataOffline();
            this.isCurrentView = true;
        } else {
            this.loadScreenData();
            if (this.canEditBip()) {
                this.scheduleAutoSave();
            }
        }
    }

     // Auto-save every 60s when in Draft view and draft model exists
     private scheduleAutoSave() {
        this.autoSaveHandle = setTimeout(() => {
            if (!this.isCurrentView && this.draftModel) {
                // Silent autosave without reloading to prevent UI blinking
                this.saveSilently(true);
            }
        }, 60000);
    };

    public onIsCurrentViewChange(e: any) {
        if (!this.canEditBip()) {
            this.isCurrentView = true;
            return;
        }
        const movingToCurrent = e?.value === true && this.isCurrentView === false;
        if (!this.isOffline() && movingToCurrent && this.draftModel) {
            // Force a silent save before switching to Current view
            this.saveSilently();
        }

        this.isCurrentView = e.value;
    }

    // Keep child view in sync with parent when local toggle is hidden
    ngOnChanges(): void {
        if (!this.canEditBip()) {
            this.isCurrentView = true;
            return;
        }
        if (!this.showDraftToggle()) {
            this.isCurrentView = this.startCurrentView();
        }
    }

    ngOnDestroy(): void {
        if (this.isOffline()) return;
        // Final silent save when leaving page if in Draft
        if (!this.isCurrentView && this.draftModel) {
            this.saveSilently();
        }
        if (this.autoSaveHandle) {
            clearTimeout(this.autoSaveHandle);
            this.autoSaveHandle = null;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(event: Event) {
        if (this.isOffline()) return;
        if (!this.isCurrentView && this.draftModel) {
            // Attempt silent save (best-effort)
            if (!this.saving) {
                this.saveSilently();
            }
        }
    }

    private isOffline(): boolean {
        return this.useOfflineData() || this.offlineDataService.isInOfflineMode();
    }

    private loadScreenData() {
        const patientIdNum = Number(this.patientID());
        const behaviorIdNum = this.behaviorID();

        this.loading.set(true);
        this.showValidationMessage = false;

        this.staffApi.GetBehaviorBipInfoScreenData({ patientID: patientIdNum, patientBehaviorID: behaviorIdNum }).subscribe({
            next: (data) => {
                this.functions.set((data?.Functions ?? []) as BehaviorFunctionModel[]);

                this.antecedentTemplates.set(this.sortTemplates((data?.AntecedentTemplates ?? []) as NameDescriptionModel[]));
                this.consequenceTemplates.set(this.sortTemplates((data?.ConsequenceTemplates ?? []) as NameDescriptionModel[]));
                this.replacementTemplates.set(this.sortTemplates((data?.ReplacementTemplates ?? []) as NameDescriptionModel[]));

                this.currentModel = (data?.CurrentModel ?? null) as BehaviorBipInfoModel | null;

                const draft = this.normalizeDraftModel((data?.DraftModel ?? null) as BehaviorBipInfoModel | null, patientIdNum, behaviorIdNum);
                this.draftModel = draft;

                this.antecedentRows.set(this.buildAntecedentRows(draft.Antecedents ?? [], this.antecedentTemplates()));
                this.consequenceRows.set(this.buildConsequenceRows(draft.Consequences ?? [], this.consequenceTemplates()));
                this.replacementRows.set(this.buildReplacementRows(draft.Replacements ?? [], this.replacementTemplates()));

                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
            }
        });
    }

    private sortTemplates(list: NameDescriptionModel[]): NameDescriptionModel[] {
        const items = (list ?? []).filter(x => !!x && !!(x.Name || '').trim());
        items.sort((a, b) => {
            const an = (a.Name || '').trim();
            const bn = (b.Name || '').trim();
            const ao = an.toLowerCase() === 'other';
            const bo = bn.toLowerCase() === 'other';
            if (ao && !bo) return 1;
            if (!ao && bo) return -1;
            return an.localeCompare(bn);
        });
        if (!items.some(x => (x.Name || '').trim().toLowerCase() === 'other')) {
            items.push({ Name: 'Other', Description: '' } as any);
        }
        return items;
    }

    private normalizeDraftModel(model: BehaviorBipInfoModel | null, patientId: number, behaviorId: number): BehaviorBipInfoModel {
        const base = model ?? ({
            PatientID: patientId,
            PatientBehaviorID: behaviorId,
            BIPID: 0,
            Definition: '',
            Context: '',
            Precursors: '',
            Antecedents: [],
            Consequences: [],
            Replacements: [],
            FunctionIDs: [],
            Functions: []
        } as any);

        const normalized = {
            ...base,
            PatientID: patientId,
            PatientBehaviorID: behaviorId,
            Definition: (base as any).Definition ?? '',
            Context: (base as any).Context ?? '',
            Precursors: (base as any).Precursors ?? '',
            Antecedents: (base as any).Antecedents ?? [],
            Consequences: (base as any).Consequences ?? [],
            Replacements: (base as any).Replacements ?? [],
            FunctionIDs: (base as any).FunctionIDs ?? [],
            Functions: (base as any).Functions ?? []
        } as BehaviorBipInfoModel;

        this.syncFunctionsFromIds(normalized);
        return normalized;
    }

    private loadDataOffline() {
        const patientIdNum = Number(this.patientID());
        this.loading.set(true);
        const data = this.offlineDataService.getOfflinePatientData(patientIdNum);
        if (data) {
            const infos = data.behaviorBipInfos || [];
            const current = infos.find(i => (i?.PatientBehaviorID ?? 0) === this.behaviorID());
            this.currentModel = current ?? null;
        } else {
            this.currentModel = null;
        }

        // Keep a non-null draft model in offline mode to avoid template errors if user toggles views.
        this.draftModel = this.normalizeDraftModel(null, patientIdNum, this.behaviorID());
        this.antecedentTemplates.set(this.sortTemplates([{ Name: 'Other', Description: '' } as any]));
        this.consequenceTemplates.set(this.sortTemplates([{ Name: 'Other', Description: '' } as any]));
        this.replacementTemplates.set(this.sortTemplates([{ Name: 'Other', Description: '' } as any]));
        this.antecedentRows.set([]);
        this.consequenceRows.set([]);
        this.replacementRows.set([]);

        this.loading.set(false);
    }

    private buildAntecedentRows(items: any[], templates: NameDescriptionModel[]) {
        return (items ?? []).map(a => {
            const savedName = (a?.Name ?? '').trim();
            const tpl = templates.find(t => this.normalizeName(t.Name) === this.normalizeName(savedName));
            if (tpl) {
                const desc = (a?.Description ?? tpl.Description ?? '') as string;
                return this.createStrategyRow({
                    id: a?.ID ?? null,
                    name: tpl.Name ?? savedName,
                    description: desc,
                    patientBehaviorAntecedentID: a?.PatientBehaviorAntecedentID ?? null
                });
            }
            const desc = (a?.Description ?? '') as string;
            return this.createStrategyRow({
                id: a?.ID ?? null,
                name: 'Other',
                customName: savedName || undefined,
                description: desc,
                patientBehaviorAntecedentID: a?.PatientBehaviorAntecedentID ?? null
            });
        });
    }

    private buildConsequenceRows(items: any[], templates: NameDescriptionModel[]) {
        return (items ?? []).map(c => {
            const savedName = (c?.Name ?? '').trim();
            const tpl = templates.find(t => this.normalizeName(t.Name) === this.normalizeName(savedName));
            if (tpl) {
                const desc = (c?.Description ?? tpl.Description ?? '') as string;
                return this.createStrategyRow({
                    id: c?.ID ?? null,
                    name: tpl.Name ?? savedName,
                    description: desc,
                    patientBehaviorConsequenceID: c?.PatientBehaviorConsequenceID ?? null
                });
            }
            const desc = (c?.Description ?? '') as string;
            return this.createStrategyRow({
                id: c?.ID ?? null,
                name: 'Other',
                customName: savedName || undefined,
                description: desc,
                patientBehaviorConsequenceID: c?.PatientBehaviorConsequenceID ?? null
            });
        });
    }

    private buildReplacementRows(items: any[], templates: NameDescriptionModel[]) {
        return (items ?? []).map(r => {
            const savedName = (r?.Name ?? '').trim();
            const tpl = templates.find(t => this.normalizeName(t.Name) === this.normalizeName(savedName));
            if (tpl) {
                const desc = (r?.Description ?? tpl.Description ?? '') as string;
                return this.createStrategyRow({
                    id: r?.ID ?? null,
                    name: tpl.Name ?? savedName,
                    description: desc,
                    patientBehaviorReplacementID: r?.PatientBehaviorReplacementID ?? null
                });
            }
            const desc = (r?.Description ?? '') as string;
            return this.createStrategyRow({
                id: r?.ID ?? null,
                name: 'Other',
                customName: savedName || undefined,
                description: desc,
                patientBehaviorReplacementID: r?.PatientBehaviorReplacementID ?? null
            });
        });
    }

    private createStrategyRow(row: Omit<BehaviorBipStrategyRow, 'clientKey'>): BehaviorBipStrategyRow {
        this.strategyRowKeySeed += 1;
        return {
            ...row,
            clientKey: `strategy-${this.strategyRowKeySeed}`
        };
    }

    addAntecedentRow() {
        const av = this.availableAntecedentTemplates(this.antecedentRows().length);
        const first = av.find(t => (t.Name || '').toLowerCase() !== 'other');
        const tpl = first ?? av.find(t => (t.Name || '').toLowerCase() === 'other');
        const name = tpl?.Name || 'Other';
        const initial = (name || '').toLowerCase() === 'other' ? '' : (tpl?.Description || '');
        this.antecedentRows.update(rows => [...rows, this.createStrategyRow({ name, description: initial, id: null, patientBehaviorAntecedentID: null })]);
    }

    removeAntecedentRow(index: number) {
        if (!this.canEditBip()) return;
        //Get the ID of the antecedent to delete
        const antecedentID = this.antecedentRows()[index].id;
        if (antecedentID) {
            this.staffApi.DeleteAntecedentFromBehaviorBIPInfo({ Behavior_BIPInfo_AntecedentID: antecedentID }).subscribe({ next: () => {
                this.antecedentRows.update(rows => rows.filter((_, i) => i !== index));
            } });
        } else {
            this.antecedentRows.update(rows => rows.filter((_, i) => i !== index));
        }

    }

    onAntecedentTemplateChange(index: number, name: string) {
        const tpl = this.antecedentTemplates().find(t => t.Name === name);
        this.antecedentRows.update(rows => {
            const next = [...rows];
            const initial = tpl?.Description || '';
            next[index] = { ...next[index], name, description: initial, patientBehaviorAntecedentID: null };
            return next;
        });
    }

    addConsequenceRow() {
        const av = this.availableConsequenceTemplates(this.consequenceRows().length);
        const first = av.find(t => (t.Name || '').toLowerCase() !== 'other');
        const tpl = first ?? av.find(t => (t.Name || '').toLowerCase() === 'other');
        const name = tpl?.Name || 'Other';
        const initial = (name || '').toLowerCase() === 'other' ? '' : (tpl?.Description || '');
        this.consequenceRows.update(rows => [...rows, this.createStrategyRow({ name, description: initial, id: null, patientBehaviorConsequenceID: null })]);
    }


    removeConsequenceRow(index: number) {
        if (!this.canEditBip()) return;
        //Get the ID of the consequence to delete
        const consequenceID = this.consequenceRows()[index].id;
        if (consequenceID) {
            this.staffApi.DeleteConsequenceFromBehaviorBIPInfo({ Behavior_BIPInfo_ConsequenceID: consequenceID }).subscribe({ next: () => {
                this.consequenceRows.update(rows => rows.filter((_, i) => i !== index));
            } });
        } else {
            this.consequenceRows.update(rows => rows.filter((_, i) => i !== index));
        }
    }

    onConsequenceTemplateChange(index: number, name: string) {
        const tpl = this.consequenceTemplates().find(t => t.Name === name);
        this.consequenceRows.update(rows => {
            const next = [...rows];
            const initial = tpl?.Description || '';
            next[index] = { ...next[index], name, description: initial, patientBehaviorConsequenceID: null };
            return next;
        });
    }

    private normalizeName(name?: string | null): string {
        return (name || '').trim().toLowerCase();
    }
    private isBlank(value?: string | null): boolean {
        return this.normalizeName(value) === '';
    }

    availableAntecedentTemplates(index: number) {
        const taken = new Set(this.antecedentRows().map((r, i) => i === index ? '' : this.normalizeName(r.name)).filter(Boolean));
        return this.antecedentTemplates().filter(t => {
            const n = this.normalizeName(t.Name);
            return n === 'other' || !taken.has(n);
        });
    }

    availableConsequenceTemplates(index: number) {
        const taken = new Set(this.consequenceRows().map((r, i) => i === index ? '' : this.normalizeName(r.name)).filter(Boolean));
        return this.consequenceTemplates().filter(t => {
            const n = this.normalizeName(t.Name);
            return n === 'other' || !taken.has(n);
        });
    }

    availableReplacementTemplates(index: number) {
        const taken = new Set(this.replacementRows().map((r, i) => i === index ? '' : this.normalizeName(r.name)).filter(Boolean));
        return this.replacementTemplates().filter(t => {
            const n = this.normalizeName(t.Name);
            return n === 'other' || !taken.has(n);
        });
    }

    // Validation helpers
    antecedentNameInvalid(index: number): boolean {
        const row = this.antecedentRows()[index];
        return this.isBlank(row?.name);
    }
    antecedentOtherInvalid(index: number): boolean {
        const row = this.antecedentRows()[index];
        return this.normalizeName(row?.name) === 'other' && this.isBlank(row?.customName);
    }
    antecedentDescInvalid(index: number): boolean {
        const row = this.antecedentRows()[index];
        return this.isBlank(row?.description);
    }

    consequenceNameInvalid(index: number): boolean {
        const row = this.consequenceRows()[index];
        return this.isBlank(row?.name);
    }
    consequenceOtherInvalid(index: number): boolean {
        const row = this.consequenceRows()[index];
        return this.normalizeName(row?.name) === 'other' && this.isBlank(row?.customName);
    }
    consequenceDescInvalid(index: number): boolean {
        const row = this.consequenceRows()[index];
        return this.isBlank(row?.description);
    }

    replacementNameInvalid(index: number): boolean {
        const row = this.replacementRows()[index];
        return this.isBlank(row?.name);
    }
    replacementOtherInvalid(index: number): boolean {
        const row = this.replacementRows()[index];
        return this.normalizeName(row?.name) === 'other' && this.isBlank(row?.customName);
    }
    replacementDescInvalid(index: number): boolean {
        const row = this.replacementRows()[index];
        return this.isBlank(row?.description);
    }

    hasValidationErrors(): boolean {
        const ant = this.antecedentRows();
        for (let i = 0; i < ant.length; i++) {
            if (this.antecedentNameInvalid(i) || this.antecedentOtherInvalid(i) || this.antecedentDescInvalid(i)) return true;
        }
        const cons = this.consequenceRows();
        for (let i = 0; i < cons.length; i++) {
            if (this.consequenceNameInvalid(i) || this.consequenceOtherInvalid(i) || this.consequenceDescInvalid(i)) return true;
        }
        const repl = this.replacementRows();
        for (let i = 0; i < repl.length; i++) {
            if (this.replacementNameInvalid(i) || this.replacementOtherInvalid(i) || this.replacementDescInvalid(i)) return true;
        }
        return false;
    }

    addReplacementRow() {
        const av = this.availableReplacementTemplates(this.replacementRows().length);
        const first = av.find(t => (t.Name || '').toLowerCase() !== 'other');
        const tpl = first ?? av.find(t => (t.Name || '').toLowerCase() === 'other');
        const name = tpl?.Name || 'Other';
        const initial = (name || '').toLowerCase() === 'other' ? '' : (tpl?.Description || '');
        this.replacementRows.update(rows => [...rows, this.createStrategyRow({ name, description: initial, id: null, patientBehaviorReplacementID: null })]);
    }

    removeReplacementRow(index: number) {
        if (!this.canEditBip()) return;
        const replacementID = this.replacementRows()[index].id;
        
        if (replacementID) {
            this.staffApi.DeleteReplacementFromBehaviorBIPInfo({ Behavior_BIPInfo_ReplacementID: replacementID }).subscribe({ next: () => {
                this.replacementRows.update(rows => rows.filter((_, i) => i !== index));
            } });
        } else {
            this.replacementRows.update(rows => rows.filter((_, i) => i !== index));
        }
    }

    onReplacementTemplateChange(index: number, name: string) {
        const tpl = this.replacementTemplates().find(t => t.Name === name);
        this.replacementRows.update(rows => {
            const next = [...rows];
            const initial = tpl?.Description || '';
            next[index] = { ...next[index], name, description: initial, patientBehaviorReplacementID: null };
            return next;
        });
    }

    ensureDraftModel(): BehaviorBipInfoModel | null {
        if (this.draftModel) return this.draftModel;
        // If no draft exists, we cannot create one without a Draft BIPID returned by server
        this.alerts.info('No draft BIP available. Start a draft BIP to edit.');
        return null;
    }

    toggleId(list: number[] | undefined, id: number | null | undefined): number[] {
        const targetId = id ?? 0;
        const next = new Set<number>([...(list ?? [])]);
        if (next.has(targetId)) next.delete(targetId); else next.add(targetId);
        return Array.from(next);
    }

    getSaveObservable() {
        if (!this.canEditBip()) return;
        const model = this.ensureDraftModel();
        if (!model) return;

        this.showValidationMessage = false;
        model.PatientID = Number(this.patientID());
        model.PatientBehaviorID = this.behaviorID();
        model.Antecedents = this.antecedentRows().map(r => ({
            ID: r.id ?? null,
            Name: (r.customName && this.normalizeName(r.name) === 'other') ? r.customName : r.name,
            Description: r.description,
            PatientBehaviorAntecedentID: r.patientBehaviorAntecedentID ?? null
        }));
        model.Antecedents = model.Antecedents.filter(a => a.Name?.toLowerCase() !== 'other');
        
        model.Consequences = this.consequenceRows().map(r => ({
            ID: r.id ?? null,
            Name: (r.customName && this.normalizeName(r.name) === 'other') ? r.customName : r.name,
            Description: r.description,
            PatientBehaviorConsequenceID: r.patientBehaviorConsequenceID ?? null
        }));
        model.Consequences = model.Consequences.filter(c => c.Name?.toLowerCase() !== 'other');

        model.Replacements = this.replacementRows().map(r => ({
            ID: r.id ?? null,
            Name: (r.customName && this.normalizeName(r.name) === 'other') ? r.customName : r.name,
            Description: r.description,
            PatientBehaviorReplacementID: r.patientBehaviorReplacementID ?? null
        }));
        model.Replacements = model.Replacements.filter(r => r.Name?.toLowerCase() !== 'other');

        this.syncFunctionsFromIds(model);

        return this.staffApi.SaveBehaviorBIPInfo({ body: model });
    }

    saveSilently(autosaving: boolean = false) {
        if (this.isOffline() || !this.canEditBip()) return;
        this.saving = true;
        this.getSaveObservable()?.subscribe({
            next: (saved) => {
                this.applySavedDraft(saved);
                this.saving = false;
                if (autosaving) {
                    this.scheduleAutoSave();
                }
            },
            error: () => {
                this.saving = false;
                if (autosaving) {
                    this.scheduleAutoSave();
                }
            }
        });
    }

    save() {
        if (this.isOffline() || !this.canEditBip()) return;
        if (this.hasValidationErrors()) {
            this.showValidationMessage = true;
            return;
        }
        this.saving = true;
        this.getSaveObservable()?.subscribe({
            next: saved => {
                this.applySavedDraft(saved);
                this.alerts.success('Saved');
                this.saving = false;
            },
            error: () => {
                this.alerts.error('Failed to save');
                this.saving = false;
            }
        });
    }

    private applySavedDraft(saved: BehaviorBipInfoModel) {
        const patientIdNum = Number(this.patientID());
        const behaviorIdNum = this.behaviorID();
        const normalized = this.normalizeDraftModel(saved ?? null, patientIdNum, behaviorIdNum);
        if (this.draftModel) {
            this.draftModel.ID = normalized.ID;
            this.draftModel.BIPID = normalized.BIPID;
            this.draftModel.PatientID = normalized.PatientID;
            this.draftModel.PatientBehaviorID = normalized.PatientBehaviorID;
            this.draftModel.FunctionIDs = normalized.FunctionIDs;
            this.draftModel.Functions = normalized.Functions;
        } else {
            this.draftModel = normalized;
        }
        this.showValidationMessage = false;
        this.updateRowIdsFromSaved(normalized);
    }

    onFunctionIdsChange(ids: number[] | null) {
        if (!this.draftModel) return;
        this.draftModel.FunctionIDs = ids ?? [];
        this.syncFunctionsFromIds(this.draftModel);
        if (this.expandedFunctionId != null && !(ids ?? []).includes(this.expandedFunctionId)) {
            this.expandedFunctionId = null;
        }
    }

    getDraftSelectedFunctions(): BehaviorFunctionModel[] {
        const ids = new Set(this.draftModel?.FunctionIDs ?? []);
        return this.functions().filter(f => f.ID != null && ids.has(f.ID));
    }

    getDraftFunctionEditItems(): { functionId: number; name: string; class: string; role: number | null; roleBadge: string | null }[] {
        const ids = (this.draftModel?.FunctionIDs ?? []).filter(x => x > 0);
        const items = ids.map(functionId => {
            const fn = this.functions().find(f => f.ID === functionId);
            const role = this.getFunctionRole(functionId);
            return {
                functionId,
                name: fn?.FunctionName ?? '',
                class: getBipFunctionCssClass(fn?.FunctionName),
                role,
                roleBadge: getBipFunctionRoleBadge(role)
            };
        });
        return items.sort((a, b) => compareBipFunctionRoleOrder(
            { role: a.role, functionID: a.functionId },
            { role: b.role, functionID: b.functionId }
        ));
    }

    getFunctionRole(functionId?: number | null): number | null {
        if (!functionId || !this.draftModel) return null;
        return this.draftModel.Functions?.find(f => f.FunctionID === functionId)?.Role ?? null;
    }

    onFunctionChipClick(functionId: number, event: Event) {
        event.stopPropagation();
        this.expandedFunctionId = this.expandedFunctionId === functionId ? null : functionId;
    }

    toggleFunctionRole(functionId: number, role: number, event: Event) {
        event.stopPropagation();
        if (!this.draftModel) return;
        this.syncFunctionsFromIds(this.draftModel);
        const link = this.draftModel.Functions?.find(f => f.FunctionID === functionId);
        if (!link) return;
        link.Role = link.Role === role ? null : role;
    }

    @HostListener('document:click')
    onDocumentClick() {
        this.expandedFunctionId = null;
    }

    getRoleBadgeForDisplay(roleLabel?: string | null): string | null {
        if (roleLabel === 'Primary') return 'P';
        if (roleLabel === 'Secondary') return 'S';
        return null;
    }

    getRoleTooltip(roleLabelOrBadge?: string | null): string {
        if (roleLabelOrBadge === 'Primary' || roleLabelOrBadge === 'P') {
            return 'Primary — main function for strategies';
        }
        if (roleLabelOrBadge === 'Secondary' || roleLabelOrBadge === 'S') {
            return 'Secondary — watch for this function';
        }
        return '';
    }

    private syncFunctionsFromIds(model: BehaviorBipInfoModel) {
        const ids = (model.FunctionIDs ?? []).filter(x => x > 0);
        const existing = model.Functions ?? [];
        model.Functions = ids.map(id => {
            const prev = existing.find(f => f.FunctionID === id);
            return { FunctionID: id, Role: prev?.Role ?? null } as BehaviorBipFunctionLinkModel;
        });
        model.FunctionIDs = ids;
    }

    private updateRowIdsFromSaved(saved: BehaviorBipInfoModel) {
        const antecedentIdByKey = new Map<string, number | null>();
        for (const a of (saved.Antecedents ?? [])) {
            antecedentIdByKey.set(this.strategyKeyFromSaved(a?.Name ?? ''), a?.ID ?? null);
        }
        this.antecedentRows.update(rows => {
            const next = [...rows];
            for (const r of next) {
                if (r.id) continue;
                const key = this.strategyKeyFromRow(r.name, r.customName);
                const id = antecedentIdByKey.get(key) ?? null;
                if (id) r.id = id;
            }
            return next;
        });

        const consequenceIdByKey = new Map<string, number | null>();
        for (const c of (saved.Consequences ?? [])) {
            consequenceIdByKey.set(this.strategyKeyFromSaved(c?.Name ?? ''), c?.ID ?? null);
        }
        this.consequenceRows.update(rows => {
            const next = [...rows];
            for (const r of next) {
                if (r.id) continue;
                const key = this.strategyKeyFromRow(r.name, r.customName);
                const id = consequenceIdByKey.get(key) ?? null;
                if (id) r.id = id;
            }
            return next;
        });

        const replacementIdByKey = new Map<string, number | null>();
        for (const r of (saved.Replacements ?? [])) {
            replacementIdByKey.set(this.strategyKeyFromSaved(r?.Name ?? ''), r?.ID ?? null);
        }
        this.replacementRows.update(rows => {
            const next = [...rows];
            for (const row of next) {
                if (row.id) continue;
                const key = this.strategyKeyFromRow(row.name, row.customName);
                const id = replacementIdByKey.get(key) ?? null;
                if (id) row.id = id;
            }
            return next;
        });
    }

    private strategyKeyFromSaved(name: string): string {
        return this.normalizeName(name);
    }

    private strategyKeyFromRow(name: string, customName?: string): string {
        const isOther = this.normalizeName(name) === 'other';
        return this.normalizeName(isOther ? (customName ?? '') : name);
    }

    getCurrentFunctions(): BipFunctionDisplayItem[] {
        return mapBehaviorBipFunctionsToDisplay(
            this.functions(),
            this.currentModel?.Functions,
            this.currentModel?.FunctionIDs
        );
    }

    getDraftFunctionsPreview(): BipFunctionDisplayItem[] {
        return mapBehaviorBipFunctionsToDisplay(
            this.functions(),
            this.draftModel?.Functions,
            this.draftModel?.FunctionIDs
        );
    }

    trackByStrategyRow(index: number, row: BehaviorBipStrategyRow): string {
        return row.clientKey;
    }

    editorId(section: string, row?: BehaviorBipStrategyRow): string {
        const key = row?.clientKey ?? 'summary';
        return `behavior-${this.behaviorID()}-${section}-${key}`;
    }

    // Build anchor id for strategies to support deep-linking/scrolling from Quick Reference
    strategyAnchor(kind: 'A' | 'R' | 'C', item: { ID?: number | null; Name?: string | null }): string {
        const key = (item?.ID != null) ? String(item.ID) : this.slug(item?.Name ?? '');
        return `${kind}-${this.behaviorID()}-${key}`;
    }

    private slug(value: string): string {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    }
}


